/**
 * ViewLifecycle — per-render cleanup coordinator for PWA views.
 *
 * Each `renderXxxView()` constructs one instance, stashes it in a module-level
 * `let activeLc`, and the exported `destroyXxxView()` calls `activeLc?.cleanup()`.
 * Resources are registered via factory-style API so a "created but forgot to
 * clean up" foot-gun becomes physically impossible — the same call that creates
 * the resource registers its teardown.
 *
 * Closes the bug class behind the 2026-04-30 retro-audit on WS registries
 * (single-side `compute()` fix missed 3 of 4 registries — only discipline catches
 * those). Same shape: 4 views, each with 6-12 cleanup sites, one of them gets
 * missed → leak → next mount sees stale subscriber / stuck inflight / stacked
 * overlay. With `lc` you can only forget the registration entirely, not the
 * teardown half.
 *
 * `cleanup()` is idempotent (multiple calls are safe), LIFO (last-registered
 * runs first — mirrors stack order, important for resources that depend on
 * earlier ones), and survives-throw (a thrown teardown does not stop later
 * teardowns from running).
 */

export interface ViewLifecycle {
  /** Wrap `window.setInterval`; the returned handle is captured for `cleanup()`. */
  setInterval(fn: () => void, ms: number): void;
  /** Wrap `window.setTimeout`; cleanup() cancels if not yet fired. */
  setTimeout(fn: () => void, ms: number): void;
  /** Wrap `EventTarget.addEventListener`; cleanup removes it. */
  addListener<K extends string>(
    target: EventTarget,
    type: K,
    fn: (ev: Event) => void,
    options?: AddEventListenerOptions | boolean,
  ): void;
  /** Adopt an arbitrary disposable — cleanup() calls it. */
  adopt(dispose: () => void): void;
  /** Escape hatch — same as adopt(), named for code-search readability. */
  onDestroy(fn: () => void): void;
  /** Run all registered teardowns LIFO, survives-throw, idempotent. */
  cleanup(): void;
}

export function createViewLifecycle(): ViewLifecycle {
  const teardowns: Array<() => void> = [];
  let done = false;

  const register = (fn: () => void): void => {
    if (done) {
      // After cleanup, any late registration is run immediately so we don't
      // silently leak. Defensive — should not happen in practice.
      try { fn(); } catch { /* swallow — see cleanup() contract */ }
      return;
    }
    teardowns.push(fn);
  };

  return {
    setInterval(fn, ms) {
      const id = window.setInterval(fn, ms);
      register(() => clearInterval(id));
    },
    setTimeout(fn, ms) {
      const id = window.setTimeout(fn, ms);
      register(() => clearTimeout(id));
    },
    addListener(target, type, fn, options) {
      target.addEventListener(type, fn, options);
      register(() => target.removeEventListener(type, fn, options));
    },
    adopt(dispose) {
      register(dispose);
    },
    onDestroy(fn) {
      register(fn);
    },
    cleanup() {
      if (done) return;
      done = true;
      // LIFO — pop until empty, ignore individual throws so a single bad
      // teardown can't keep the rest from running. Mirrors `useEffect` cleanup
      // ordering in React (and the WS-registry destroy ordering pinned in
      // CLAUDE.md "Concurrency contracts").
      while (teardowns.length > 0) {
        const fn = teardowns.pop()!;
        try { fn(); } catch (e) {
          // Log but continue. Future enhancement: route to logError() if we
          // wire a side-channel — for now console is sufficient (matches the
          // existing global onerror handler in main.ts).
          console.error('[ViewLifecycle] teardown threw:', e);
        }
      }
    },
  };
}
